import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../contexts/PermissionContext';
import { hrAPI } from '../lib/api';
import api from '../lib/api';

export default function Taxes() {
  const { hasPermission } = usePermissions();
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [formData, setFormData] = useState({ name: '', rate: '', description: '' });
  const [teamMembersForCreatedBy, setTeamMembersForCreatedBy] = useState([]);

  useEffect(() => {
    loadTaxes();
  }, []);

  const loadTaxes = async () => {
    try {
      const response = await api.get('/api/taxes/');
      setTaxes(response.data?.items || response.data || []);
      
      try {
        const teamMembersRes = await hrAPI.getTeamMembers();
        setTeamMembersForCreatedBy(teamMembersRes.data || []);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load taxes:', error);
      setLoading(false);
    }
  };

  const handleOpenDialog = (tax = null) => {
    if (tax) {
      setEditingTax(tax);
      setFormData({ name: tax.name, rate: tax.rate, description: tax.description || '' });
    } else {
      setEditingTax(null);
      setFormData({ name: '', rate: '', description: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTax) {
        await api.put(`/api/taxes/${editingTax.id}/`, formData);
      } else {
        await api.post('/api/taxes/', formData);
      }
      toast.success(editingTax ? 'Tax updated successfully' : 'Tax created successfully');
      setShowDialog(false);
      loadTaxes();
    } catch (error) {
      console.error('Failed to save tax:', error);
      toast.error('Failed to save tax');
    }
  };

  const handleDelete = async (taxId) => {
    if (!confirm('Are you sure you want to delete this tax?')) return;

    try {
      await api.delete(`/api/taxes/${taxId}/`);
      toast.success('Tax deleted successfully');
      loadTaxes();
    } catch (error) {
      console.error('Failed to delete tax:', error);
      toast.error('Failed to delete tax');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="taxes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Tax Settings</h1>
        {hasPermission('account', 'multiple_tax', 'create') && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-tax-button">
            <Plus className="mr-2 h-4 w-4" />
            Add Tax
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tax Rates</CardTitle>
          <CardDescription>Manage tax rates for quotes, invoices, and jobs</CardDescription>
        </CardHeader>
        <CardContent>
          {taxes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tax rates configured. Add your first tax rate to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax Name</TableHead>
                  <TableHead>Rate (%)</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxes.map((tax) => (
                  <TableRow key={tax.id}>
                    <TableCell className="font-medium">{tax.name}</TableCell>
                    <TableCell>{tax.rate}%</TableCell>
                    <TableCell className="text-muted-foreground">{tax.description || 'N/A'}</TableCell>
                    <TableCell>
                      {tax.created_by_name || (() => {
                        const member = teamMembersForCreatedBy.find(m => m.id === tax.created_by);
                        return member ? member.name : 'Unknown';
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      {hasPermission('account', 'multiple_tax', 'update') && (
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(tax)} data-testid={`edit-tax-${tax.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {hasPermission('account', 'multiple_tax', 'delete') && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(tax.id)} data-testid={`delete-tax-${tax.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTax ? 'Edit Tax' : 'Add New Tax'}</DialogTitle>
            <DialogDescription>
              Configure tax rates to be used across the CRM system
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Tax Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., GST, VAT, Sales Tax"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">Tax Rate (%)</Label>
                <Input
                  id="rate"
                  type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                  step="0.01"
                  placeholder="e.g., 18.00"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="e.g., Goods and Services Tax"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">{editingTax ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
